import React, { useEffect, useState, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { adminService } from '../../../../services';
import type { AdminOrder, OrderStatus } from '../../../../types/admin';
import './AdminOrders.css';

// ── Helpers ──────────────────────────────────────────────────

const formatDate = (iso: string) =>
  new Date(iso).toLocaleDateString('it-IT', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });

const formatCurrency = (n: number) =>
  n.toLocaleString('it-IT', { style: 'currency', currency: 'EUR' });

const STATUS_CONFIG: Record<
  OrderStatus,
  {
    label: string;
    className: string;
    icon: string;
  }
> = {
  completed: {
    label: 'Completato',
    className: 'aord-badge--completed',
    icon: 'bi-check-circle-fill',
  },
  pending: {
    label: 'In attesa',
    className: 'aord-badge--pending',
    icon: 'bi-clock-fill',
  },
  refunded: {
    label: 'Rimborsato',
    className: 'aord-badge--refunded',
    icon: 'bi-arrow-counterclockwise',
  },
};

const PROVIDER_CONFIG: Record<string, { label: string; icon: string }> = {
  stripe: { label: 'Stripe', icon: 'bi-credit-card' },
  paypal: { label: 'PayPal', icon: 'bi-paypal' },
  satispay: { label: 'Satispay', icon: 'bi-phone' },
};

type StatusFilter = 'all' | OrderStatus;
type SortField = 'createdAt' | 'amount' | 'userLastName' | 'status';
type SortDir = 'asc' | 'desc';

// ── Skeleton ─────────────────────────────────────────────────

const TableSkeleton: React.FC = () => (
  <div className="aord-skeleton-rows">
    {Array.from({ length: 5 }).map((_, i) => (
      <div key={i} className="aord-skeleton-row">
        <div className="aord-skeleton-cell">
          <div className="aord-skeleton aord-skeleton--name" />
          <div className="aord-skeleton aord-skeleton--email" />
        </div>
        <div className="aord-skeleton aord-skeleton--course" />
        <div className="aord-skeleton aord-skeleton--badge" />
        <div className="aord-skeleton aord-skeleton--amount" />
        <div className="aord-skeleton aord-skeleton--date" />
        <div className="aord-skeleton aord-skeleton--badge" />
      </div>
    ))}
  </div>
);

// ── Main component ────────────────────────────────────────────

const AdminOrders: React.FC = () => {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const basePath = pathname.startsWith('/superadmin')
    ? '/superadmin'
    : '/admin';

  const [orders, setOrders] = useState<AdminOrder[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [sortField, setSortField] = useState<SortField>('createdAt');
  const [sortDir, setSortDir] = useState<SortDir>('desc');
  const [expandedId, setExpandedId] = useState<string | null>(null);

  useEffect(() => {
    adminService
      .getOrders()
      .then(setOrders)
      .catch(() => setError('Impossibile caricare gli ordini.'))
      .finally(() => setLoading(false));
  }, []);

  // ── KPI ──────────────────────────────────────────────────
  const kpi = useMemo(() => {
    const completed = orders.filter((o) => o.status === 'completed');
    const refunded = orders.filter((o) => o.status === 'refunded');
    const pending = orders.filter((o) => o.status === 'pending');
    const revenue = completed.reduce((s, o) => s + o.amount, 0);
    const refunds = refunded.reduce((s, o) => s + o.amount, 0);
    return { total: orders.length, revenue, refunds, pending: pending.length };
  }, [orders]);

  // ── Filtro + sort ────────────────────────────────────────
  const filtered = useMemo(() => {
    let list = [...orders];

    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter(
        (o) =>
          o.userFirstName.toLowerCase().includes(q) ||
          o.userLastName.toLowerCase().includes(q) ||
          o.userEmail.toLowerCase().includes(q) ||
          o.courseTitle.toLowerCase().includes(q) ||
          o.id.toLowerCase().includes(q)
      );
    }

    if (statusFilter !== 'all') {
      list = list.filter((o) => o.status === statusFilter);
    }

    list.sort((a, b) => {
      let va = '',
        vb = '';
      if (sortField === 'createdAt') {
        va = a.createdAt;
        vb = b.createdAt;
      }
      if (sortField === 'amount') {
        return sortDir === 'asc' ? a.amount - b.amount : b.amount - a.amount;
      }
      if (sortField === 'userLastName') {
        va = a.userLastName;
        vb = b.userLastName;
      }
      if (sortField === 'status') {
        va = a.status;
        vb = b.status;
      }
      return sortDir === 'asc' ? va.localeCompare(vb) : vb.localeCompare(va);
    });

    return list;
  }, [orders, search, statusFilter, sortField, sortDir]);

  const handleSort = (field: SortField) => {
    if (sortField === field) setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    else {
      setSortField(field);
      setSortDir('desc');
    }
  };

  const SortIcon: React.FC<{ field: SortField }> = ({ field }) => {
    if (sortField !== field)
      return (
        <i className="bi bi-arrow-down-up aord-sort-icon aord-sort-icon--inactive" />
      );
    return (
      <i
        className={`bi ${sortDir === 'asc' ? 'bi-arrow-up' : 'bi-arrow-down'} aord-sort-icon`}
      />
    );
  };

  return (
    <div className="aord-page">
      {/* ── Header ── */}
      <div className="aord-page-header">
        <div>
          <h1 className="aord-page-title">Ordini</h1>
          <p className="aord-page-sub">
            {loading ? '…' : `${filtered.length} di ${orders.length} ordini`}
          </p>
        </div>
      </div>

      {/* ── KPI cards ── */}
      {!loading && !error && (
        <div className="aord-kpi-grid">
          <div className="aord-kpi">
            <i className="bi bi-receipt aord-kpi__icon" />
            <div>
              <p className="aord-kpi__label">Totale ordini</p>
              <p className="aord-kpi__value">{kpi.total}</p>
            </div>
          </div>
          <div className="aord-kpi aord-kpi--highlight">
            <i className="bi bi-cash-stack aord-kpi__icon" />
            <div>
              <p className="aord-kpi__label">Ricavi completati</p>
              <p className="aord-kpi__value">{formatCurrency(kpi.revenue)}</p>
            </div>
          </div>
          <div className="aord-kpi">
            <i className="bi bi-arrow-counterclockwise aord-kpi__icon" />
            <div>
              <p className="aord-kpi__label">Rimborsi</p>
              <p className="aord-kpi__value">{formatCurrency(kpi.refunds)}</p>
            </div>
          </div>
          <div className="aord-kpi">
            <i className="bi bi-clock aord-kpi__icon" />
            <div>
              <p className="aord-kpi__label">In attesa</p>
              <p className="aord-kpi__value">{kpi.pending}</p>
            </div>
          </div>
        </div>
      )}

      {/* ── Toolbar ── */}
      <div className="aord-toolbar">
        <div className="aord-search-wrap">
          <i className="bi bi-search aord-search-icon" aria-hidden="true" />
          <input
            type="search"
            className="aord-search"
            placeholder="Cerca per utente, corso, ID…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            aria-label="Cerca ordini"
          />
          {search && (
            <button
              className="aord-search-clear"
              onClick={() => setSearch('')}
              aria-label="Cancella ricerca"
            >
              <i className="bi bi-x" />
            </button>
          )}
        </div>

        <div className="aord-filters">
          {(['all', 'completed', 'pending', 'refunded'] as StatusFilter[]).map(
            (s) => (
              <button
                key={s}
                className={`aord-filter-btn ${statusFilter === s ? 'aord-filter-btn--active' : ''}`}
                onClick={() => setStatusFilter(s)}
              >
                {s === 'all' ? 'Tutti' : STATUS_CONFIG[s].label}
                {s !== 'all' && (
                  <span className="aord-filter-count">
                    {orders.filter((o) => o.status === s).length}
                  </span>
                )}
              </button>
            )
          )}
        </div>
      </div>

      {/* ── Error ── */}
      {error && (
        <div className="aord-error" role="alert">
          <i className="bi bi-exclamation-triangle me-2" />
          {error}
        </div>
      )}

      {/* ── Contenuto ── */}
      {loading ? (
        <TableSkeleton />
      ) : filtered.length === 0 ? (
        <div className="aord-empty">
          <i className="bi bi-receipt aord-empty-icon" />
          <p className="aord-empty-title">Nessun ordine trovato</p>
          <p className="aord-empty-sub">
            Prova a cambiare i filtri di ricerca.
          </p>
          <button
            className="aord-action-btn"
            onClick={() => {
              setSearch('');
              setStatusFilter('all');
            }}
          >
            Reimposta filtri
          </button>
        </div>
      ) : (
        <>
          {/* Desktop table */}
          <div className="aord-table-wrap">
            <table className="aord-table">
              <thead>
                <tr>
                  <th>
                    <button
                      className="aord-th-btn"
                      onClick={() => handleSort('userLastName')}
                    >
                      Utente <SortIcon field="userLastName" />
                    </button>
                  </th>
                  <th>Corso</th>
                  <th>
                    <button
                      className="aord-th-btn"
                      onClick={() => handleSort('status')}
                    >
                      Stato <SortIcon field="status" />
                    </button>
                  </th>
                  <th>
                    <button
                      className="aord-th-btn"
                      onClick={() => handleSort('amount')}
                    >
                      Importo <SortIcon field="amount" />
                    </button>
                  </th>
                  <th>
                    <button
                      className="aord-th-btn"
                      onClick={() => handleSort('createdAt')}
                    >
                      Data <SortIcon field="createdAt" />
                    </button>
                  </th>
                  <th>Provider</th>
                  <th aria-label="Azioni" />
                </tr>
              </thead>
              <tbody>
                {filtered.map((o) => {
                  const sc = STATUS_CONFIG[o.status];
                  const pc = PROVIDER_CONFIG[o.paymentProvider];
                  return (
                    <tr key={o.id}>
                      <td>
                        <div className="aord-user-cell">
                          <p className="aord-user-name">
                            {o.userFirstName} {o.userLastName}
                          </p>
                          <p className="aord-user-email">{o.userEmail}</p>
                        </div>
                      </td>
                      <td className="aord-course-cell">{o.courseTitle}</td>
                      <td>
                        <span className={`aord-badge ${sc.className}`}>
                          <i className={`bi ${sc.icon} me-1`} />
                          {sc.label}
                        </span>
                      </td>
                      <td className="aord-amount-cell">
                        {formatCurrency(o.amount)}
                      </td>
                      <td className="aord-date-cell">
                        {formatDate(o.createdAt)}
                      </td>
                      <td>
                        <span className="aord-provider">
                          <i className={`bi ${pc?.icon} me-1`} />
                          {pc?.label ?? o.paymentProvider}
                        </span>
                      </td>
                      <td>
                        <button
                          className="aord-icon-btn"
                          onClick={() =>
                            navigate(`${basePath}/utenti/${o.userId}`)
                          }
                          title="Vedi utente"
                          aria-label="Vedi utente"
                        >
                          <i className="bi bi-person" />
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Mobile cards */}
          <div className="aord-mobile-list">
            {filtered.map((o) => {
              const sc = STATUS_CONFIG[o.status];
              const pc = PROVIDER_CONFIG[o.paymentProvider];
              const isOpen = expandedId === o.id;

              return (
                <div key={o.id} className="aord-mobile-card">
                  <button
                    className="aord-mobile-card__header"
                    onClick={() => setExpandedId(isOpen ? null : o.id)}
                    aria-expanded={isOpen}
                  >
                    <div className="aord-mobile-card__left">
                      <p className="aord-user-name">
                        {o.userFirstName} {o.userLastName}
                      </p>
                      <p className="aord-user-email">{o.courseTitle}</p>
                    </div>
                    <div className="aord-mobile-card__right">
                      <span className={`aord-badge ${sc.className}`}>
                        {sc.label}
                      </span>
                      <span className="aord-amount-cell">
                        {formatCurrency(o.amount)}
                      </span>
                      <i
                        className={`bi ${isOpen ? 'bi-chevron-up' : 'bi-chevron-down'} aord-chevron`}
                      />
                    </div>
                  </button>

                  {isOpen && (
                    <div className="aord-mobile-card__body">
                      <div className="aord-detail-row">
                        <span className="aord-detail-label">Email</span>
                        <span className="aord-detail-value">{o.userEmail}</span>
                      </div>
                      <div className="aord-detail-row">
                        <span className="aord-detail-label">Data</span>
                        <span className="aord-detail-value">
                          {formatDate(o.createdAt)}
                        </span>
                      </div>
                      <div className="aord-detail-row">
                        <span className="aord-detail-label">Provider</span>
                        <span className="aord-detail-value">
                          <i className={`bi ${pc?.icon} me-1`} />
                          {pc?.label ?? o.paymentProvider}
                        </span>
                      </div>
                      <div className="aord-detail-row">
                        <span className="aord-detail-label">ID ordine</span>
                        <code className="aord-code">{o.id}</code>
                      </div>
                      <button
                        className="aord-action-btn aord-action-btn--full"
                        onClick={() =>
                          navigate(`${basePath}/utenti/${o.userId}`)
                        }
                      >
                        <i className="bi bi-person me-2" />
                        Vedi profilo utente
                      </button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
};

export default AdminOrders;
